import { ApiParameter, get, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";

const baseUrl = "/api/v1";
const baseWarehouseURL = `${baseUrl}/warehouse`;

export interface AddDefaultWHParameter extends ApiParameter {
    id: string;
}

export interface UpdatePartNumberParameter extends ApiParameter {
    erpNumber: string;
    customerPartNumber: string;
}

export interface ProductCategoriesParameter {
    productIds: string[];
    type: string;
}

export interface ProductCategoriesModel {
    id: string;
    erpNumber: string;
    categories: {
        item_category: string;
        item_category2: string;
        item_category3: string;
    };
    categoriesIds: {
        item_category: string;
        item_category2: string;
        item_category3: string;
    };
    brandName: string | null;
    customProperties: Record<string, unknown>;
    attributes: {
        [key: string]: string;
    };
}

export interface StatementRecord {
    label: string;
    balance: number;
}

export interface StatementTableModel {
    records: StatementRecord[];
}

export interface SystemListValue {
    id: string;
    name: string;
    description: string;
    additionalInfo: string;
    systemListId: string;
    deactivateOn: string | null;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    customProperties: any[];
}

export interface SystemList {
    id: string;
    name: string;
    description: string;
    additionalInfo: string;
    deactivateOn: string | null;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    customProperties: any[];
    values: SystemListValue[];
}

export async function getAllWarehouses(): Promise<ServiceResult<WarehouseModel[]>> {
    const result = await get<WarehouseModel[]>(`${baseWarehouseURL}/getAllWarehouses/`);

    return {
        successful: true,
        result,
    };
}

export async function getDefaultWarehouse(): Promise<ServiceResult<WarehouseModel>> {
    const result = await get<WarehouseModel>(`${baseWarehouseURL}`);

    return {
        successful: true,
        result,
    };
}

export async function setDefaultWarehouse(parameter: AddDefaultWHParameter): Promise<ServiceResult<boolean>> {
    try {
        const result = await post<boolean>(
            `${baseWarehouseURL}/setDefault?warehouseId=${parameter.id}`,
            parameter.id as any,
        );

        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getProductCategories(
    params: ProductCategoriesParameter,
): Promise<ServiceResult<ProductCategoriesModel[]>> {
    try {
        const result = await post<{ productProperties: ProductCategoriesModel[] }>(
            `${baseUrl}/productproperties`,
            params as any,
        );

        return {
            result: result.productProperties,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getStatementTable() {
    const result = await get<StatementTableModel>(`${baseUrl}/statement`);

    return {
        successful: true,
        result,
    };
}

export async function setCustomerPartNumber(erpNumber: string, customerPartNumber: string): Promise<any> {
    try {
        const result = await post<any>(`${baseUrl}/customer/partnumber`, {
            erpNumber,
            customerPartNumber,
        });

        return {
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getSystemList(name: string): Promise<ServiceResult<SystemList>> {
    try {
        const result = await get<SystemList>(`${baseUrl}/systemlist/getbyname?systemListName=${name}`);

        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}
